import {
	KeyboardAvoidingView,
	Modal,
	StyleSheet,
	Text,
	View,
	type GestureResponderEvent,
	type NativeSyntheticEvent,
	type NativeTouchEvent,
	type TextInputKeyPressEvent,
} from 'react-native';
import { HelperText, TextInput } from 'react-native-paper';
import Translations from 'src/core/utils/Translations';
import { PrimaryButton, SecondaryButton } from 'src/presentation/components/molecules/Button';
import dialogStyles from 'src/presentation/dialogs/styles/DialogStyles';
import SelectField, { type PickerItem } from 'src/presentation/components/molecules/SelectField';
import Colors from 'src/core/utils/Colors';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';

interface Props {
	buttonText?: string;
	buttonTextClaim?: string;
	emailText: string;
	items: PickerItem[];
	messageError?: string;
	messageText: string | null;
	nameText: string;
	onKeyPress?: (e: TextInputKeyPressEvent) => void;
	onPress: (event: GestureResponderEvent) => void;
	onPressClaim?: (event: GestureResponderEvent) => void;
	onSendMessageLoading?: boolean;
	onPressIn?: (e: NativeSyntheticEvent<NativeTouchEvent>) => void;
	reservationNumberText: string;
	selectedValue?: string | null;
	setMessageText: (text: string) => void;
	setReservationNumberText: (text: string) => void;
	setSelectedValue: (value: string, index: number) => void;
	setSubjectText: (text: string) => void;
	subjectText: string | null;
	title?: string;
	visible?: boolean;
}

const SendMessageDialog = ({
	buttonText = Translations.cancel,
	buttonTextClaim = Translations.send,
	emailText,
	items,
	messageError,
	messageText,
	nameText,
	onKeyPress,
	onPress,
	onPressClaim,
	onSendMessageLoading = false,
	onPressIn,
	reservationNumberText,
	selectedValue,
	setMessageText,
	setReservationNumberText,
	setSelectedValue,
	setSubjectText,
	subjectText,
	title = Translations.sendAMessage,
	visible,
}: Props) => {
	return (
		<Modal animationType="fade" style={dialogStyles.modalView} transparent={true} visible={visible}>
			<KeyboardAvoidingView behavior="padding" style={dialogStyles.centeredView}>
				<View style={dialogStyles.modalCard}>
					<Text style={dialogStyles.modalTitle}>{title}</Text>

					<View style={styles.divider} />

					<View style={styles.sendAsBanner}>
						<Text style={styles.sendAsText}>
							{Translations.sendAs}
							&nbsp;
							<Text style={styles.sendAsName}>
								{nameText} &lt;
								{emailText}
								&gt;
							</Text>
						</Text>
					</View>

					<Text style={styles.resortLabel}>{Translations.thermenResortGeneralFormTitle}</Text>

					<SelectField
						items={items}
						placeholder={{ label: Translations.selectResort, value: null }}
						style={styles.helperOffset}
						value={selectedValue ?? ''}
						onValueChange={setSelectedValue}
					/>

					<Text style={styles.reservationNumberLabel}>{Translations.reservationNumberFormTitle}</Text>

					<TextInput
						activeUnderlineColor={Colors.green}
						label={Translations.reservationNumber}
						selectionColor={Colors.gray600}
						style={styles.reservationNumberInput}
						value={reservationNumberText}
						onChangeText={setReservationNumberText}
					/>

					<TextInput
						activeUnderlineColor={Colors.green}
						label={Translations.subject}
						selectionColor={Colors.gray600}
						style={styles.subjectInput}
						value={subjectText ?? ''}
						onChangeText={setSubjectText}
					/>

					<TextInput
						activeUnderlineColor={Colors.green}
						label={Translations.message}
						multiline={true}
						numberOfLines={3}
						selectionColor={Colors.gray600}
						style={styles.messageInput}
						value={messageText ?? ''}
						onChangeText={setMessageText}
						onKeyPress={onKeyPress}
						onPressIn={onPressIn}
					/>

					<HelperText style={{ marginBottom: Dimens.padding20 }} type="error" visible={!!messageError}>
						{messageError}
					</HelperText>

					<View style={{ flexDirection: 'row' }}>
						<SecondaryButton
							extraStyle={{ paddingHorizontal: Dimens.padding10, flex: Dimens.flex1 }}
							label={buttonText}
							onPress={onPress}
						/>
						<PrimaryButton
							extraStyle={{ paddingHorizontal: Dimens.padding10, flex: Dimens.flex1 }}
							label={buttonTextClaim}
							loading={onSendMessageLoading}
							onPress={onPressClaim}
						/>
					</View>
				</View>
			</KeyboardAvoidingView>
		</Modal>
	);
};

const styles = StyleSheet.create({
	helperOffset: {
		marginBottom: Dimens.padding12,
		marginTop: Dimens.paddingMinus2,
	},
	divider: {
		width: '100%',
		height: Dimens.size1,
		marginBottom: Dimens.padding24,
	},
	sendAsBanner: {
		backgroundColor: Colors.gray200,
		paddingHorizontal: Dimens.padding14,
		paddingVertical: Dimens.padding10,
		marginBottom: Dimens.padding12,
		borderRadius: Dimens.borderRadius4,
	},
	sendAsText: {
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize12,
		color: Colors.gray550,
	},
	sendAsName: {
		fontFamily: Fonts.barlowMedium,
		color: Colors.gray750,
	},
	resortLabel: {
		fontFamily: Fonts.barlowMedium,
		color: Colors.gray750,
		marginBottom: Dimens.padding4,
		marginHorizontal: Dimens.padding8,
		fontWeight: Dimens.fontWeight700,
	},
	reservationNumberLabel: {
		fontFamily: Fonts.barlowMedium,
		color: Colors.gray750,
		marginBottom: Dimens.padding8,
		marginHorizontal: Dimens.padding8,
		fontWeight: Dimens.fontWeight700,
	},
	reservationNumberInput: {
		backgroundColor: Colors.gray200,
		marginBottom: Dimens.padding8,
	},
	subjectInput: {
		backgroundColor: Colors.gray200,
		marginBottom: Dimens.padding10,
	},
	messageInput: {
		backgroundColor: Colors.gray200,
		minHeight: Dimens.size120,
	},
});

export default SendMessageDialog;
